import React, { useMemo, useState, useEffect } from 'react';
import { HiPlus, HiPencil, HiTrash, HiX, HiSearch } from 'react-icons/hi';
import ConfirmDialog from './ConfirmDialog';
import Notification from './Notification';
import { useNotification } from '../hooks/useNotification';
import { SkeletonTable } from './Skeleton';
import apiService from '../services/api';
import { normalizeApiData } from '../utils/apiHelpers';
import PhoneInput from './PhoneInput';
import CNPJInput from './CNPJInput';
import CEPInput from './CEPInput';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSAO_MAP } from '../types';
import './Suppliers.css';

const Suppliers = () => {
  const { userRole, loading: permissionsLoading } = usePermissions();
  const isSuppliersAdmin = useMemo(() => {
    if (permissionsLoading) return false;
    if (!userRole) return false;
    return PERMISSAO_MAP[Number(userRole.permissaos_id)] === 'Admin';
  }, [userRole, permissionsLoading]);

  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [formData, setFormData] = useState({
    razao_social: '',
    nome_fantasia: '',
    email: '',
    telefone: '',
    cnpj: '',
    endereco: '',
    cidade: '',
    uf: '',
    cep: '',
    bairro: '',
    numero: '',
    complemento: ''
  });
  const [editingId, setEditingId] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [supplierToDelete, setSupplierToDelete] = useState(null);
  const { notifications, removeNotification, showSuccess, showWarning } = useNotification();

  useEffect(() => {
    loadSuppliers();
  }, [searchTerm]);

  const loadSuppliers = async () => {
    try {
      setLoading(true);
      const data = await apiService.getSuppliers({
        search: searchTerm,
      });
      const suppliersList = normalizeApiData(data);
      setSuppliers(suppliersList);
      setLoading(false);
    } catch (error) {
      console.error('Erro ao carregar fornecedores:', error);
      setSuppliers([]);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
    if (editingId) {
        await apiService.updateSupplier(editingId, {
          razao_social: formData.razao_social,
          nome_fantasia: formData.nome_fantasia || null,
          email: formData.email,
          telefone: formData.telefone,
          cnpj: formData.cnpj || null,
          endereco: formData.endereco || null,
          cidade: formData.cidade || null,
          uf: formData.uf || null,
          cep: formData.cep || null,
          bairro: formData.bairro || null,
          numero: formData.numero || null,
          complemento: formData.complemento || null,
          categoria: 'geral',
        });
        showSuccess('Fornecedor atualizado com sucesso!');
    } else {
        await apiService.createSupplier({
          razao_social: formData.razao_social,
          nome_fantasia: formData.nome_fantasia || null,
          email: formData.email,
          telefone: formData.telefone,
          cnpj: formData.cnpj || null,
          endereco: formData.endereco || null,
          cidade: formData.cidade || null,
          uf: formData.uf || null,
          cep: formData.cep || null,
          bairro: formData.bairro || null,
          numero: formData.numero || null,
          complemento: formData.complemento || null,
          categoria: 'geral',
        });
        showSuccess('Fornecedor criado com sucesso!');
    }
    handleCloseModal();
      loadSuppliers();
    } catch (error) {
      console.error('Erro ao salvar fornecedor:', error);
      showWarning(error.message || 'Erro ao salvar fornecedor');
    }
  };

  const handleEdit = (supplier) => {
    setFormData({
      razao_social: supplier.razao_social || supplier.nome || '',
      nome_fantasia: supplier.nome_fantasia || '',
      email: supplier.email || '',
      telefone: supplier.telefone || supplier.phone || '',
      cnpj: supplier.cnpj || supplier.documento || '',
      endereco: supplier.endereco || supplier.address || '',
      cidade: supplier.cidade || '',
      uf: supplier.uf || '',
      cep: supplier.cep || '',
      bairro: supplier.bairro || '',
      numero: supplier.numero || '',
      complemento: supplier.complemento || '',
    });
    setEditingId(supplier.id);
    setShowModal(true);
  };

  const handleDelete = (id) => {
    if (!isSuppliersAdmin) return;
    setSupplierToDelete(id);
    setShowConfirmDialog(true);
  };

  const confirmDelete = async () => {
    if (!supplierToDelete) {
      showWarning('ID do fornecedor não encontrado');
      setShowConfirmDialog(false);
      return;
    }
    if (!isSuppliersAdmin) return;

    try {
      console.log('Excluindo fornecedor com ID:', supplierToDelete);
      await apiService.deleteSupplier(supplierToDelete);
    setShowConfirmDialog(false);
    setSupplierToDelete(null);
    showSuccess('Fornecedor excluído com sucesso!');
      loadSuppliers();
    } catch (error) {
      console.error('Erro ao excluir fornecedor:', error);
      console.error('Detalhes do erro:', {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status
      });
      const errorMessage = error?.message || 'Erro ao excluir fornecedor. Verifique se o fornecedor não está sendo usado em outros registros.';
      showWarning(errorMessage);
      setShowConfirmDialog(false);
      setSupplierToDelete(null);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({
      razao_social: '',
      nome_fantasia: '',
      email: '',
      telefone: '',
      cnpj: '',
      endereco: '',
      cidade: '',
      uf: '',
      cep: '',
      bairro: '',
      numero: '',
      complemento: ''
    });
    setEditingId(null);
  };

  // A filtragem já é feita pela API, mas mantemos aqui para compatibilidade
  const filteredSuppliers = Array.isArray(suppliers) ? suppliers.filter(supplier =>
    (supplier.razao_social || supplier.nome || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (supplier.nome_fantasia || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (supplier.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (supplier.cnpj || supplier.documento || '').includes(searchTerm)
  ) : [];

  const totalPages = Math.ceil(filteredSuppliers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedSuppliers = filteredSuppliers.slice(startIndex, endIndex);

  return (
    <div className="page-container">
      <div className="breadcrumb">
        <span>Painel</span>
        <span>Fornecedores</span>
      </div>

      <div className="page-header">
        <div>
          <h1>Fornecedores</h1>
          <p>Gerencie seus fornecedores</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          <HiPlus size={20} />
          Novo Fornecedor
        </button>
      </div>

      <div className="page-actions">
        <div className="search-box">
          <HiSearch className="search-icon" size={20} />
          <input
            type="text"
            placeholder="Buscar fornecedores..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>
      </div>

      <div className="card">
        {loading ? (
          <SkeletonTable rows={8} columns={6} />
        ) : paginatedSuppliers.length === 0 ? (
          <div className="empty-state">
            <p>{searchTerm ? 'Nenhum fornecedor encontrado' : 'Nenhum fornecedor cadastrado'}</p>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Nome</th>
                    <th>Email</th>
                    <th>Telefone</th>
                    <th>CNPJ</th>
                    <th>Endereço</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedSuppliers.map((supplier, index) => (
                    <tr key={supplier.id}>
                      <td>{startIndex + index + 1}</td>
                      <td><strong>{supplier.razao_social || supplier.nome || 'N/A'}</strong></td>
                      <td>{supplier.email || 'N/A'}</td>
                      <td>{supplier.telefone || supplier.phone || 'N/A'}</td>
                      <td>{supplier.cnpj || supplier.documento || 'N/A'}</td>
                      <td>{supplier.endereco || supplier.address || 'N/A'}</td>
                      <td>
                        <div className="actions">
                          <button
                            className="btn-icon btn-edit"
                            onClick={() => handleEdit(supplier)}
                            title="Editar"
                          >
                            <HiPencil size={18} />
                          </button>
                          {isSuppliersAdmin && (
                            <button
                              className="btn-icon btn-delete"
                              onClick={() => handleDelete(supplier.id)}
                              title="Excluir"
                            >
                              <HiTrash size={18} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="table-footer">
              <div className="pagination-info">
                {filteredSuppliers.length > 0 ? (
                  <span>
                    Mostrando {(currentPage - 1) * itemsPerPage + 1} a {Math.min(currentPage * itemsPerPage, filteredSuppliers.length)} de {filteredSuppliers.length} registro{filteredSuppliers.length !== 1 ? 's' : ''}
                  </span>
                ) : (
                  <span>Nenhum registro encontrado</span>
                )}
              </div>
              <div className="items-per-page">
                <span>Mostrar:</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
              <div className="pagination">
                <button
                  className="pagination-btn"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  ‹
                </button>
                <span className="pagination-info">
                  Página {currentPage} de {totalPages || 1}
                </span>
                <button
                  className="pagination-btn"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages || totalPages === 0}
                >
                  ›
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingId ? 'Editar Fornecedor' : 'Novo Fornecedor'}</h2>
              <button className="modal-close" onClick={handleCloseModal}>
                <HiX size={24} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-group">
                <label>Razão Social *</label>
                <input
                  type="text"
                  value={formData.razao_social}
                  onChange={(e) => setFormData({ ...formData, razao_social: e.target.value })}
                  required
                  placeholder="Digite a razão social"
                />
              </div>
              <div className="form-group">
                <label>Nome Fantasia</label>
                <input
                  type="text"
                  value={formData.nome_fantasia}
                  onChange={(e) => setFormData({ ...formData, nome_fantasia: e.target.value })}
                  placeholder="Digite o nome fantasia"
                />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  placeholder="email@exemplo.com"
                />
              </div>
              <div className="form-group">
                <label>Telefone *</label>
                <PhoneInput
                  value={formData.telefone}
                  onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                  required
                  placeholder="Número do telefone"
                />
              </div>
              <div className="form-group">
                <label>CNPJ</label>
                <CNPJInput
                  value={formData.cnpj}
                  onChange={(e) => setFormData({ ...formData, cnpj: e.target.value })}
                  placeholder="00.000.000/0000-00"
                />
              </div>
              <div className="form-group">
                <label>CEP</label>
                <CEPInput
                  value={formData.cep}
                  onChange={(e) => setFormData({ ...formData, cep: e.target.value })}
                  onAddressChange={(address) => {
                    setFormData({
                      ...formData,
                      cep: address.cep || formData.cep,
                      endereco: address.endereco || formData.endereco,
                      bairro: address.bairro || formData.bairro,
                      cidade: address.cidade || formData.cidade,
                      uf: address.uf || formData.uf,
                      complemento: address.complemento || formData.complemento
                    });
                  }}
                  placeholder="00000-000"
                />
              </div>
              <div className="form-group">
                <label>Endereço</label>
                <input
                  type="text"
                  value={formData.endereco}
                  onChange={(e) => setFormData({ ...formData, endereco: e.target.value })}
                  placeholder="Rua, Avenida, etc."
                />
              </div>
              <div className="form-group">
                <label>Número</label>
                <input
                  type="text"
                  value={formData.numero}
                  onChange={(e) => setFormData({ ...formData, numero: e.target.value })}
                  placeholder="123"
                />
              </div>
              <div className="form-group">
                <label>Complemento</label>
                <input
                  type="text"
                  value={formData.complemento}
                  onChange={(e) => setFormData({ ...formData, complemento: e.target.value })}
                  placeholder="Apto, Sala, etc."
                />
              </div>
              <div className="form-group">
                <label>Bairro</label>
                <input
                  type="text"
                  value={formData.bairro}
                  onChange={(e) => setFormData({ ...formData, bairro: e.target.value })}
                  placeholder="Nome do bairro"
                />
              </div>
              <div className="form-group">
                <label>Cidade</label>
                <input
                  type="text"
                  value={formData.cidade}
                  onChange={(e) => setFormData({ ...formData, cidade: e.target.value })}
                  placeholder="Nome da cidade"
                />
              </div>
              <div className="form-group">
                <label>UF</label>
                <input
                  type="text"
                  value={formData.uf}
                  onChange={(e) => setFormData({ ...formData, uf: e.target.value.toUpperCase() })}
                  maxLength="2"
                  placeholder="SP"
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingId ? 'Atualizar' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={showConfirmDialog}
        title="Confirmar Exclusão"
        message="Tem certeza que deseja excluir este fornecedor?"
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowConfirmDialog(false);
          setSupplierToDelete(null);
        }}
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
      />

      <div className="notifications-container">
        {notifications.map(notification => (
          <Notification
            key={notification.id}
            message={notification.message}
            type={notification.type}
            duration={notification.duration}
            onClose={() => removeNotification(notification.id)}
          />
        ))}
      </div>
    </div>
  );
};

export default Suppliers;